/**
 * A point carried by a finger, placed as exactly as a finger can place anything:
 *
 *  • it moves by the finger's travel from where it was picked up, never jumping to the finger
 *    (the finger's contact point lies ~4 mm from where its owner aims: Holz & Baudisch 2011);
 *  • a slow finger moves it finely — down to a third of the finger's travel — so the last
 *    millimetre is easy, and it comes out from under the fingertip (Vogel & Baudisch's Shift,
 *    2007; iOS scrubbing);
 *  • it takes hold of a snap within `rIn` — only while the finger is slowing to settle, never as it
 *    sweeps past (on a busy plan a sweeping point would catch on every line) — and keeps it until
 *    the finger has clearly gone on (`rOut`), then catches up with the finger rather than leaping
 *    (Fernquist et al.'s Oh Snap, 2011);
 *  • on lift-off, the finger's roll in the last moments is ignored (Potter et al. 1988; Vogel &
 *    Baudisch 2007): where it was a moment before counts.
 */
import type { Vec } from '../../shared/doc.ts';

const SLOW = 40; // screen px/s: at or below this the point moves at the finest gain
const FAST = 320; // at or above this, one to one
const FINEST = 0.33;
const SETTLING = 200; // screen px/s: below this the finger is settling, and a snap may take hold
const SETTLE_MS = 70; // lift-off: the finger's last moments …
const SETTLE_PX = 6; // … count as roll when it moved no more than this (screen px)

export interface PreciseMove {
  /** Where the point is now (image px). */
  at: Vec;
  /** It has just taken hold of a snap. */
  took: boolean;
}

export class PreciseDrag {
  /** Where the point is (image px): the snap it holds, or the finger's point with any catch-up still to go. */
  at: Vec;
  /** Where the finger's travel alone puts it. */
  raw: Vec;
  /** The snap it holds, if any. */
  hold: Vec | null = null;
  /** The finger is moving slowly: the point moves finely. */
  fine = false;
  #lag: Vec = { x: 0, y: 0 };
  #last: { x: number; y: number; t: number };
  #speed = FAST;
  #hist: { t: number; finger: Vec; at: Vec }[] = [];

  constructor(start: Vec, finger: { x: number; y: number; t: number }) {
    this.at = { ...start };
    this.raw = { ...start };
    this.#last = { x: finger.x, y: finger.y, t: finger.t };
    this.#hist.push({ t: finger.t, finger: { x: finger.x, y: finger.y }, at: { ...start } });
  }

  /**
   * The finger moved to `finger` (screen px, with its time). `scale`: screen px per image px.
   * `snap` gives the snap for a point, if one is near.
   */
  move(finger: { x: number; y: number; t: number }, scale: number, snap: (p: Vec) => Vec | null, rIn: number, rOut: number): PreciseMove {
    const dx = finger.x - this.#last.x,
      dy = finger.y - this.#last.y;
    const dt = Math.max(1, finger.t - this.#last.t);
    const v = (Math.hypot(dx, dy) / dt) * 1000;
    this.#speed = 0.6 * this.#speed + 0.4 * v;
    const k = Math.max(0, Math.min(1, (this.#speed - SLOW) / (FAST - SLOW)));
    const gain = FINEST + (1 - FINEST) * k;
    this.fine = gain < 0.9;
    this.#last = { x: finger.x, y: finger.y, t: finger.t };
    this.raw = { x: this.raw.x + (dx * gain) / scale, y: this.raw.y + (dy * gain) / scale };
    // a released snap is caught up with at half the finger's pace again
    const lagLen = Math.hypot(this.#lag.x, this.#lag.y);
    if (lagLen > 0) {
      const give = (0.5 * Math.hypot(dx, dy)) / scale;
      const keep = Math.max(0, lagLen - give) / lagLen;
      this.#lag = { x: this.#lag.x * keep, y: this.#lag.y * keep };
    }
    let took = false;
    if (this.hold && Math.hypot(this.raw.x - this.hold.x, this.raw.y - this.hold.y) > rOut) {
      this.#lag = { x: this.hold.x - this.raw.x, y: this.hold.y - this.raw.y };
      this.hold = null;
    }
    if (!this.hold && this.#speed < SETTLING) {
      const s = snap(this.raw);
      if (s && Math.hypot(s.x - this.raw.x, s.y - this.raw.y) <= rIn) {
        this.hold = s;
        this.#lag = { x: 0, y: 0 };
        took = true;
      }
    } else if (this.hold) {
      // a nearer snap of the same kind may take over (sliding along a line keeps it on the line)
      const s = snap(this.raw);
      if (s && Math.hypot(s.x - this.raw.x, s.y - this.raw.y) <= rIn) this.hold = s;
    }
    this.at = this.hold ?? { x: this.raw.x + this.#lag.x, y: this.raw.y + this.#lag.y };
    this.#hist.push({ t: finger.t, finger: { x: finger.x, y: finger.y }, at: { ...this.at } });
    while (this.#hist.length > 2 && this.#hist[0]!.t < finger.t - 400) this.#hist.shift();
    return { at: this.at, took };
  }

  /** The plan moved on under a still finger (image px): the point goes along with the finger. */
  shift(dx: number, dy: number): void {
    this.raw = { x: this.raw.x + dx, y: this.raw.y + dy };
    this.at = { x: this.at.x + dx, y: this.at.y + dy };
    if (this.hold) {
      // it lets go of what it held: the finger has gone on
      this.#lag = { x: this.hold.x + dx - this.raw.x, y: this.hold.y + dy - this.raw.y };
      this.hold = null;
    }
    for (const h of this.#hist) h.at = { x: h.at.x + dx, y: h.at.y + dy };
  }

  /** Where the point is placed when the finger lifts at `finger`: before the lift's roll. */
  release(finger: { x: number; y: number; t: number }): Vec {
    let before: { t: number; finger: Vec; at: Vec } | null = null;
    for (const h of this.#hist) if (h.t <= finger.t - SETTLE_MS) before = h;
    if (before && Math.hypot(finger.x - before.finger.x, finger.y - before.finger.y) <= SETTLE_PX) return before.at;
    return this.at;
  }
}
